<template>
  <card title="Commission terms">
    <div v-if="loading" class="commission-terms__muted">Loading...</div>
    <div v-else-if="loadError" class="commission-terms__error">{{ loadError }}</div>

    <el-form :disabled="!canManageAgencies" v-else label-position="top" class="commission-terms" @submit.native.prevent="save">
      <div class="row">
        <div class="col-md-4">
          <el-form-item label="Standard commission, %" :error="errors.commission_standard">
            <el-input
              v-model.trim="form.commission_standard"
              placeholder="Not set"
              clearable
              inputmode="decimal"
            >
              <template slot="append">%</template>
            </el-input>
          </el-form-item>
        </div>
        <div class="col-md-4">
          <el-form-item label="Contract currency" :error="errors.contract_currency_id">
            <el-select
              v-model="form.contract_currency_id"
              placeholder="Not set"
              no-data-text="No data"
              no-match-text="No match"
              filterable
              clearable
              class="commission-terms__full"
            >
              <el-option
                v-for="currency in currencies"
                :key="currency.id"
                :label="currencyLabel(currency)"
                :value="currency.id"
              />
            </el-select>
          </el-form-item>
        </div>
        <div class="col-md-4">
          <el-form-item label="Manual review" :error="errors.commission_manual_review">
            <el-switch v-model="form.commission_manual_review" />
            <div class="commission-terms__hint">
              Tiered or complex contracts are calculated manually — turn this on to skip automatic calculation.
            </div>
          </el-form-item>
        </div>
      </div>

      <el-form-item label="Excluded categories" :error="errors.excluded_categories">
        <el-checkbox-group v-model="form.excluded_categories">
          <el-checkbox v-for="category in availableCategories" :key="category" :label="category">
            {{ categoryLabel(category) }}
          </el-checkbox>
        </el-checkbox-group>
        <div class="commission-terms__hint">Hotel and transfer are always excluded from the commission base.</div>
        <div v-if="availableCategories.includes('transfer_hotel')" class="commission-terms__hint">
          Hotel &amp; transfer: shows the optional Hotel &amp; transfer column in the partner's Excel template. Hotel &amp;
          transfer is always deducted.
        </div>
      </el-form-item>

      <div v-if="generalError" class="commission-terms__error">{{ generalError }}</div>

      <base-button v-if="canManageAgencies" type="primary" native-type="submit" :disabled="saving">
        {{ saving ? 'Saving...' : 'Save' }}
      </base-button>
    </el-form>
  </card>
</template>

<script>
import agencyAccess from '@/components/Agencies/agencyAccess';
import APIAgencies from '@/api/modules/agencies';
import { categoryLabel, violationMessage, DECIMAL_RE, TERMS_NO_FIELDS_MESSAGE } from './commission';

const emptyForm = () => ({
  commission_standard: '',
  commission_manual_review: false,
  excluded_categories: [],
  contract_currency_id: null,
});

export default {
  mixins: [agencyAccess],
  props: {
    agencyId: { type: Number, required: true },
  },
  data: () => ({
    form: emptyForm(),
    availableCategories: [],
    currencies: [],
    errors: {},
    generalError: '',
    loadError: '',
    loading: false,
    saving: false,
  }),
  created() {
    this.load();
  },
  methods: {
    categoryLabel,
    currencyLabel(currency) {
      return currency.name ? `${currency.code} — ${currency.name}` : currency.code;
    },
    applyTerms(terms) {
      const t = terms || {};
      this.form = {
        commission_standard: t.commission_standard === null || t.commission_standard === undefined
          ? ''
          : String(t.commission_standard),
        commission_manual_review: Boolean(t.commission_manual_review),
        excluded_categories: Array.isArray(t.excluded_categories) ? [...t.excluded_categories] : [],
        contract_currency_id: t.contract_currency_id || null,
      };
      if (Array.isArray(t.available_categories)) {
        this.availableCategories = t.available_categories;
      }
      // The rules table labels fixed amounts with the saved contract currency.
      const currency = this.currencies.find((c) => c.id === this.form.contract_currency_id);
      this.$emit('currency-change', currency ? currency.code : '');
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const [terms, currencies] = await Promise.all([
          APIAgencies.getCommissionTerms(this.agencyId),
          APIAgencies.getCommissionCurrencies().catch(() => {
            this.$notify({ type: 'warning', title: 'Failed to load currencies' });
            return [];
          }),
        ]);
        this.currencies = currencies;
        this.applyTerms(terms);
      } catch (e) {
        this.loadError = e && e.is && e.is('not_found')
          ? 'Partner not found'
          : `Failed to load commission terms${e && e.code ? ` (${e.code})` : ''}`;
      } finally {
        this.loading = false;
      }
    },
    validate() {
      const errors = {};
      const value = this.form.commission_standard;
      if (value !== '') {
        const normalized = value.replace(',', '.');
        const number = parseFloat(normalized);
        if (!DECIMAL_RE.test(normalized) || !(number > 0) || number > 100) {
          errors.commission_standard = 'Enter a percentage greater than 0 and not above 100 (max 2 decimals)';
        }
      }
      return errors;
    },
    buildPayload() {
      const value = this.form.commission_standard.replace(',', '.');
      return {
        commission_standard: value === '' ? null : value,
        commission_manual_review: this.form.commission_manual_review,
        excluded_categories: this.form.excluded_categories,
        contract_currency_id: this.form.contract_currency_id || null,
      };
    },
    async save() {
      if (!this.canManageAgencies) return;
      this.generalError = '';
      this.errors = this.validate();
      if (Object.keys(this.errors).length) return;

      this.saving = true;
      try {
        const terms = await APIAgencies.saveCommissionTerms(this.agencyId, this.buildPayload());
        if (terms) this.applyTerms(terms);
        this.$notify({ type: 'success', title: 'Commission terms saved' });
      } catch (e) {
        this.handleSaveError(e);
      } finally {
        this.saving = false;
      }
    },
    handleSaveError(e) {
      if (e && e.is && (e.is('invalid_value') || e.is('validation_failed'))) {
        const fieldErrors = e.fieldErrors;
        this.errors = Object.keys(fieldErrors).reduce((acc, field) => {
          acc[field] = violationMessage(fieldErrors[field]);
          return acc;
        }, {});
        if (!Object.keys(this.errors).length) this.generalError = 'Some values are invalid';
        return;
      }
      if (e && e.is && e.is('no_fields_to_update')) {
        this.generalError = TERMS_NO_FIELDS_MESSAGE;
        return;
      }
      if (e && e.is && e.is('not_found')) {
        this.generalError = 'Partner not found';
        return;
      }
      this.generalError = `Failed to save commission terms${e && e.code ? ` (${e.code})` : ''}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.commission-terms {
  &__full {
    width: 100%;
  }

  &__hint {
    font-size: 12px;
    line-height: 1.4;
    color: #8898aa;
    margin-top: 4px;
  }

  &__error {
    color: #e3342f;
    font-size: 13px;
    margin-bottom: 12px;
  }

  ::v-deep .el-checkbox {
    margin-bottom: 6px;
  }
}

.commission-terms__muted {
  color: #888;
  font-size: 13px;
}

.commission-terms__error {
  color: #e3342f;
  font-size: 13px;
}
</style>
